import { DatePicker } from "antd";
import { DatePickerProps } from "antd";
import classNames from "classnames";
import { FieldWrapper } from "components/UI/Table/UI/InteractiveCell/components/FieldWrapper.tsx";
import { WithSaveButton } from "components/UI/Table/UI/InteractiveCell/components/WithSaveButton.tsx";
import { useInteractiveCell } from "components/UI/Table/UI/InteractiveCell/hooks/useInteractiveCell.ts";
import { FieldProps } from "components/UI/Table/UI/InteractiveCell/miscs/types.ts";
import dayjs from "dayjs";
import { FieldValues } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";

type Props<T extends FieldValues> = DatePickerProps & FieldProps<T>;

export const WithDatePicker = <T extends FieldValues = FieldValues>({ formControl, ...props }: Props<T>) => {
    const { onChange, onSave, value, className, ...restProps } = props;
    const {
        setControlledValue,
        resolvedValue,
        isFormControlled,
        handleOnSaveSideEffects,
        handleFocus,
        handleBlur,
        fieldRef,
        error,
    } = useInteractiveCell({
        formControl,
        value: value != null ? dayjs(value) : undefined,
    });

    const handleChange: DatePickerProps["onChange"] = (date, dateString) => {
        setControlledValue(date);
        onChange?.(date, dateString);
    };

    const handleSave = () => {
        handleOnSaveSideEffects();
        onSave?.(dayjs(resolvedValue as Parameters<typeof dayjs>[0]).valueOf());
    };

    return (
        <FieldWrapper
            displayValue={resolvedValue || props.defaultValue}
            ref={fieldRef}
            isError={!!error}
            errorMessage={error?.message as string}
        >
            <WithSaveButton onSave={handleSave}>
                {isFormControlled ? (
                    <FormItem {...(formControl as NonNullable<typeof formControl>)} noStyle>
                        <DatePicker
                            {...restProps}
                            className={classNames(className, "!w-full")}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                        />
                    </FormItem>
                ) : (
                    <DatePicker
                        {...restProps}
                        onChange={handleChange}
                        className={classNames(className, "!w-full")}
                        value={resolvedValue ? dayjs(resolvedValue as Parameters<typeof dayjs>[0]) : undefined}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                    />
                )}
            </WithSaveButton>
        </FieldWrapper>
    );
};
